import { __assign, __awaiter, __generator } from "tslib";
import { createAndInitializeLogger, } from '@1js/ai-hub-telemetry';
import { fetchFeatureGates } from '@office-start/feature-gates-service';
import { fetchIdentity } from '@office-start/identity-service';
import { getAppPlatform, getShellUserContext, } from '@office-start/office-experience-service/src/instrumentation/instrumentationHelpers';
import { fetchUserEntitlements, getUserEntitlements, } from '@office-start/user-entitlements-service';
import { isFirstUse } from '@office-start/user-storage-service';
import { createSingletonState } from 'singleton-state';
import { createAiHubOtelConfig } from './createAiHubOtelConfig';
var aiHubAriaTenantToken = '86e373bba78849b9a171f1691f60be36-8b939b94-65a5-48dc-bb15-7db2cdb0650f-7210'; // Note: This is the real tenant key for the AI Hub Kusto DB. Please do not copy and paste this unless you're supposed to log there.
var aiHubLoggingNamespace = 'Office.AIHub.Web.OfficeStart';
var loggerState = createSingletonState('@office-start/aihub-telemetry-service/logger', null);
export function getAiHubLogger() {
    return loggerState.get();
}
function setLogger(newLogger) {
    loggerState.set(newLogger);
}
export function getAiHubSinkConfig(_a, workload) {
    var _b, _c;
    var buildDateUtc = _a.buildDateUtc, buildVersion = _a.buildVersion, deploymentEnvironment = _a.deploymentEnvironment, ecsRing = _a.ecsRing, correlationId = _a.correlationId, browserSessionId = _a.sessionId, preferredCulture = _a.preferredCulture, flights = _a.flights, isCorpNet = _a.isCorpNet, rootPath = _a.rootPath;
    return __awaiter(this, void 0, void 0, function () {
        var userEntitlementsResult, userEntitlements, _d, _e, isAdmin, isSmb, isIw, isEdu, isEduStudent, isEduFaculty, isDynamics, identity, isFirstVisit, appPlatform, userSegment, featureGates, error_1, appName;
        return __generator(this, function (_f) {
            switch (_f.label) {
                case 0:
                    userEntitlementsResult = getUserEntitlements();
                    if (!(userEntitlementsResult === null || userEntitlementsResult === void 0 ? void 0 : userEntitlementsResult.result)) return [3 /*break*/, 1];
                    _d = userEntitlementsResult;
                    return [3 /*break*/, 3];
                case 1: return [4 /*yield*/, fetchUserEntitlements()];
                case 2:
                    _d = _f.sent();
                    _f.label = 3;
                case 3:
                    userEntitlements = (_d).result;
                    if (!userEntitlements) {
                        throw new Error('UserEntitlements not found');
                    }
                    return [4 /*yield*/, getShellUserContext(userEntitlements)];
                case 4:
                    _e = _f.sent(), isAdmin = _e.isAdmin, isSmb = _e.isSmb, isIw = _e.isIw, isEdu = _e.isEdu, isEduStudent = _e.isEduStudent, isEduFaculty = _e.isEduFaculty, isDynamics = _e.isDynamics;
                    return [4 /*yield*/, fetchIdentity()];
                case 5:
                    identity = _f.sent();
                    return [4 /*yield*/, isFirstUse()];
                case 6:
                    isFirstVisit = _f.sent();
                    appPlatform = getAppPlatform(workload);
                    userSegment = {
                        isAdmin: isAdmin,
                        isDynamics: isDynamics,
                        isEdu: isEdu,
                        isEduFaculty: isEduFaculty,
                        isEduStudent: isEduStudent,
                        isFirstVisit: isFirstVisit,
                        isIw: isIw,
                        isSmb: isSmb,
                    };
                    featureGates = undefined;
                    _f.label = 7;
                case 7:
                    _f.trys.push([7, 9, , 10]);
                    return [4 /*yield*/, fetchFeatureGates()];
                case 8:
                    featureGates = _f.sent();
                    return [3 /*break*/, 10];
                case 9:
                    error_1 = _f.sent();
                    console.error('Could not fetch feature gates:', error_1.message);
                    return [3 /*break*/, 10];
                case 10:
                    appName = 'AIHub';
                    return [2 /*return*/, createAiHubOtelConfig({
                            appName: appName,
                            appPlatform: appPlatform,
                            bundleBuildDate: buildDateUtc,
                            bundleBuildId: buildVersion,
                            isMsa: identity.isMsa,
                            puid: identity.puid,
                            sessionId: correlationId,
                            browserSessionId: browserSessionId,
                            tenantId: identity.tenantId,
                            ecsRing: ecsRing !== null && ecsRing !== void 0 ? ecsRing : 'Unknown',
                            preferredCulture: preferredCulture,
                            deploymentEnvironment: deploymentEnvironment,
                            dataBoundary: identity === null || identity === void 0 ? void 0 : identity.dataBoundary,
                            userSegment: userSegment,
                            flights: flights ? flights.split(',') : [],
                            isEudbEndpointEnabled: ((_c = (_b = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _b === void 0 ? void 0 : _b.clientOTelGates) === null || _c === void 0 ? void 0 : _c.clientOTelEudbEndpointEnabled) || false,
                            isCorpNet: isCorpNet,
                            rootPath: rootPath,
                        })];
            }
        });
    });
}
export function createAndInitializeAiHubLogger(otelSinkConfig) {
    if (getAiHubLogger() !== null || otelSinkConfig === null)
        return;
    var loggerConfiguration = {
        loggingNamespace: aiHubLoggingNamespace,
        ariaTenantToken: aiHubAriaTenantToken,
    };
    var logger = createAndInitializeLogger(loggerConfiguration, otelSinkConfig, {
        otelAriaTenantToken: aiHubAriaTenantToken,
        writeConsoleOnTransmit: true,
    });
    setLogger(logger);
    logger.sendEvent('Startup.Event.AiHubLoggerInitialized');
}
export function createAndInitializeModuleLogger(otelSinkConfig, moduleName, moduleSessionId) {
    if (!otelSinkConfig)
        return undefined;
    var loggerConfiguration = {
        loggingNamespace: aiHubLoggingNamespace,
        ariaTenantToken: aiHubAriaTenantToken,
    };
    otelSinkConfig.partCConfig = __assign(__assign({}, otelSinkConfig.partCConfig), { moduleName: moduleName, moduleSessionId: moduleSessionId });
    var logger = createAndInitializeLogger(loggerConfiguration, otelSinkConfig, {
        otelAriaTenantToken: aiHubAriaTenantToken,
        writeConsoleOnTransmit: true,
    });
    logger.sendEvent('Startup.Event.AiHubModuleLoggerInitialized');
    return logger;
}
//# sourceMappingURL=AiHubTelemetryService.js.map